/**
 * §6.4 What You Receive — all 12 platform modules included in every
 * white-label licence.
 */

import React from 'react'
import {
  Bot, LineChart, Wand2, FlaskConical, Wallet, ScanLine, GraduationCap,
  PieChart, CreditCard, UsersRound, BellRing, Cog, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { RECEIVED } from '../data'
import { TONE } from '../_shared'

const ICONS = {
  Bot, LineChart, Wand2, FlaskConical, Wallet, ScanLine, GraduationCap,
  PieChart, CreditCard, UsersRound, BellRing, Cog,
}

export default function WhatYouReceive() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>What You Receive</Eyebrow>
          <SectionHeading align="center"
            title={<>Twelve enterprise modules.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One licence.</span></>}
            subtitle="Every white-label tenant gets the entire Kalki Ai Cloud platform out of the box — no add-ons, no tier gating, no per-module fees."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {RECEIVED.map((r) => {
            const tone = TONE[r.tone] || TONE.orange
            const Icon = ICONS[r.iconKey] || Sparkles
            return (
              <div key={r.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <h4 className="text-[14.5px] font-semibold text-white leading-tight">{r.label}</h4>
                    <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5">{r.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> included
          </span>
          <p className="text-sm text-zinc-300 leading-relaxed">
            AI Platform · Trading Terminal · Strategy Builder · Backtesting · Portfolio ·
            Market Scanner · Trading Academy · Analytics · Billing · CRM · Notifications ·
            White-Label Admin — <span className="font-semibold text-white">all included</span> in every plan.
          </p>
        </div>
      </Container>
    </section>
  )
}
